"use client";

import { Button } from "@/components/ui/Button";
import { Calendar } from "@/components/ui/Calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { format, isValid, parse } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";

interface DatePickerProps {
  label: string;
  placeholder?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  id: string;
  useDays: boolean;
  isInvalid?: boolean;
  disablePortal?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  placeholder = label,
  value,
  onChange,
  id,
  useDays,
  isInvalid = false,
  disablePortal = false,
}) => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      locale: cs,
      months: [
        "Leden",
        "Únor",
        "Březen",
        "Duben",
        "Květen",
        "Červen",
        "Červenec",
        "Srpen",
        "Září",
        "Říjen",
        "Listopad",
        "Prosinec",
      ],
    },
    en: {
      locale: enUS,
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    },
  }[language];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [gridYear, setGridYear] = useState(new Date().getFullYear());
  const inputRef = useRef<HTMLInputElement>(null);

  const displayValue = value
    ? useDays
      ? format(new Date(value), "d.M.yyyy", { locale: copy.locale })
      : format(new Date(value), "MM.yyyy", { locale: copy.locale })
    : "";

  // --- useDays handlers ---
  const handleCalendarSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;
    const isoUTC = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    ).toISOString();
    onChange(isoUTC);
    setOpen(false);
    setInputValue(format(selectedDate, "d.M.yyyy", { locale: copy.locale }));
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);

    const fmt = useDays ? "d.M.yyyy" : "MM.yyyy";
    const parsed = parse(raw, fmt, new Date());
    if (isValid(parsed)) {
      const isoUTC = new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        useDays ? parsed.getDate() : 1,
      ).toISOString();
      onChange(isoUTC);
    } else if (raw === "") {
      onChange(null);
    }
  };

  // --- Month picker handler ---
  const handleMonthSelect = (monthIndex: number) => {
    const isoUTC = new Date(gridYear, monthIndex, 1).toISOString();
    onChange(isoUTC);
    setInputValue(`${String(monthIndex + 1).padStart(2, "0")}.${gridYear}`);
    setOpen(false);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const selectedMonth = value ? new Date(value).getMonth() : null;
  const selectedYear = value ? new Date(value).getFullYear() : null;

  useEffect(() => {
    if (selectedYear != null) {
      setGridYear(selectedYear);
    }
  }, [open, selectedYear]);

  return (
    <div className="relative mb-2">
      {editing ? (
        <input
          ref={inputRef}
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={() => setEditing(false)}
          aria-invalid={isInvalid}
          placeholder={useDays ? "d.M.yyyy" : "MM.yyyy"}
          className={`app-input-base mb-0 ${
            isInvalid
              ? "!border-red-500 !bg-red-50 !text-red-700 transition-all duration-300 focus:ring-red-500"
              : ""
          }`}
        />
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              aria-label={label}
              aria-invalid={isInvalid}
              className={`app-input-base mb-0 justify-between font-normal ${
                isInvalid
                  ? "!border-red-500 !bg-red-50 !text-red-700 transition-all duration-300"
                  : ""
              }`}
            >
              {displayValue ? (
                <span>{displayValue}</span>
              ) : (
                <span className="text-foreground/50">{placeholder}</span>
              )}
              <CalendarIcon className="size-4 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            disablePortal={disablePortal}
            data-date-picker-popover=""
            className="w-auto bg-background p-0"
          >
            {useDays ? (
              <Calendar
                mode="single"
                locale={copy.locale}
                selected={value ? new Date(value) : undefined}
                onSelect={handleCalendarSelect}
                initialFocus
              />
            ) : (
              <div className="p-3 w-[280px]">
                {/* Year navigator */}
                <div className="flex items-center justify-between mb-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setGridYear((y) => y - 1)}
                    className="h-8 w-8"
                  >
                    <ChevronLeftIcon className="size-4" />
                  </Button>
                  <span className="font-semibold text-sm">{gridYear}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setGridYear((y) => y + 1)}
                    className="h-8 w-8"
                  >
                    <ChevronRightIcon className="size-4" />
                  </Button>
                </div>
                {/* Month grid */}
                <div className="grid grid-cols-3 gap-1">
                  {copy.months.map((m, i) => (
                    <Button
                      key={i}
                      type="button"
                      variant={
                        selectedMonth === i && selectedYear === gridYear
                          ? "default"
                          : "ghost"
                      }
                      onClick={() => handleMonthSelect(i)}
                      className="h-auto w-full px-2 py-2 text-sm"
                    >
                      {m}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
